import React, { useCallback } from 'react'
import { FormControlLabel, Checkbox } from '@material-ui/core'

import { Button } from 'Components/Buttons/Button'
import FileDownloadIcon from 'Assets/Images/icons/fileDownload'
import {
  formatToLocalDate,
  dropTimeZoneFromDate,
} from 'shared/libraries/utils/date'
import { escapeCsvString } from 'shared/csv'
import { downloadStringAsFile } from 'Utils/Misc'
import { usePickupContext } from '../../context/PickupContext'
import { isBriefPage } from '../../utils'
import SearchAndFiltersWithCodeAutoComplete from '../SearchAndFiltersWithCodeAutoComplete'
import { days, merchNames, weeks } from './const'
import PresetToggle from './PresetToggle'
import { ROLE_DISTRIBUTOR } from '../../../../../../shared/constants'

const sanitizeStr = str => (str ? str.toLowerCase().trim() : str)

const downloadPickupAllCSV = missions => {
  if (!missions) return alert('Please wait, still loading data.')
  let csvContent = `Name,Phone Number,Email,Pickup Time\n`
  missions.forEach(m => {
    csvContent +=
      [
        escapeCsvString(m['AMBASSADOR'] || ''),
        escapeCsvString(m['ambassadorMobilePhone'] || ''),
        escapeCsvString(m['ambassadorEmail'] || ''),
        escapeCsvString(
          m['SCHEDULED PICKUP TIME']
            ? formatToLocalDate(
                new Date(dropTimeZoneFromDate(m['SCHEDULED PICKUP TIME'])),
              )
            : '',
        ),
      ].join(',') + '\n'
  })
  downloadStringAsFile(csvContent, 'pickup-all.csv')
}

export const PickupHeader = ({
  pickupType,
  rerenderAfter,
  setSearchText,
  classes,
  missions,
  availableCodes,
  givenOutCodes,
}) => {
  const {
    pickupContextState: {
      brandAmbassadors,
      clients,
      campaigns,
      selectedBrandAmbassadors,
      selectedClients,
      selectedCodes,
      selectedCampaigns,
      selectedMerchTypes,
      selectedDays,
      selectedWeeks,
      selectedPreset,
      showOnlyBooked,
      cityId,
    },
    setSelectedBrandAmbassadors,
    setSelectedClients,
    setSelectedCodes,
    setSelectedCampaigns,
    setSelectedMerchTypes,
    setSelectedDays,
    setSelectedWeeks,
    setSelectedPreset,
    setShowOnlyBooked,
    setExportBrief,
  } = usePickupContext()

  let clientHash = {}
  let campaignHash = {}
  let brandAmbassadorsHash = {}
  clients.forEach(o => (clientHash[sanitizeStr(o.name)] = o))
  campaigns.forEach(o => (campaignHash[sanitizeStr(o.name)] = o))
  brandAmbassadors.forEach(o => (brandAmbassadorsHash[sanitizeStr(o.id)] = o))

  const handlePickupBrief = () => {
    if (isBriefPage(pickupType)) {
      setExportBrief(true)
      return
    }

    downloadPickupAllCSV(missions)
  }

  const onChangeMultiAmbassadors = useCallback(
    values => {
      if (!values || !Array.isArray(values)) return
      setSelectedBrandAmbassadors(
        values.length ? values.map(key => brandAmbassadorsHash[key]) : null,
      )
    },
    [brandAmbassadorsHash],
  )

  const onChangeMultiCampaigns = useCallback(
    values => {
      if (!values || !Array.isArray(values)) return
      setSelectedCampaigns(
        values.length
          ? values.map(key => campaignHash[sanitizeStr(key)])
          : null,
      )
    },
    [campaignHash],
  )

  const onChangeMultiClients = useCallback(
    values => {
      if (!values || !Array.isArray(values)) return
      setSelectedClients(
        values.length ? values.map(key => clientHash[sanitizeStr(key)]) : null,
      )
    },
    [clientHash],
  )

  const onChangeMultiMerchTypes = useCallback(values => {
    if (!values || !Array.isArray(values)) return
    setSelectedMerchTypes(values.length ? values.map(key => key) : null)
  }, [])

  const onChangeMultiDays = useCallback(values => {
    if (!values || !Array.isArray(values)) return
    setSelectedDays(values.length ? values.map(key => key) : null)
  }, [])

  const onChangeMultiWeeks = useCallback(values => {
    if (!values || !Array.isArray(values)) return
    setSelectedWeeks(values.length ? values.map(key => key) : null)
  }, [])

  const ambassadorFilter = {
    label: 'User',
    values: brandAmbassadors.map(a => ({
      value: a.id,
      label: `${a.name} (${a.status})`,
      tag: a.userRole != ROLE_DISTRIBUTOR ? a.userRole : '',
    })),
    onChange: () => {},
    onChangeMulti: onChangeMultiAmbassadors,
    selectedValue: selectedBrandAmbassadors
      ? selectedBrandAmbassadors.map(o => o.id)
      : null,
    multiSelectedValue: selectedBrandAmbassadors
      ? selectedBrandAmbassadors.map(o => o.id)
      : [],
  }

  const campaignFilter = {
    label: 'Campaign',
    values: campaigns.map(c => c.name).sort(),
    onChange: () => {},
    onChangeMulti: onChangeMultiCampaigns,
    selectedValue: selectedCampaigns
      ? selectedCampaigns.map(o => o.name)
      : null,
    multiSelectedValue: selectedCampaigns
      ? selectedCampaigns.map(o => o.name)
      : [],
  }

  const filters = isBriefPage(pickupType)
    ? [
        ambassadorFilter,
        {
          label: 'Day',
          values: days.map(d => d.name),
          onChange: () => {},
          onChangeMulti: onChangeMultiDays,
          selectedValue: selectedDays,
          multiSelectedValue: selectedDays ? selectedDays : [],
          sorted: false,
        },
        {
          label: 'Weeks',
          values: weeks,
          onChange: () => {},
          onChangeMulti: onChangeMultiWeeks,
          selectedValue: selectedWeeks,
          multiSelectedValue: selectedWeeks ? selectedWeeks : [],
          sorted: false,
        },
      ]
    : [
        ambassadorFilter,
        {
          label: 'Client',
          values: clients.map(c => c.name).sort(),
          onChange: () => {},
          onChangeMulti: onChangeMultiClients,
          selectedValue: selectedClients
            ? selectedClients.map(o => o.name)
            : null,
          multiSelectedValue: selectedClients
            ? selectedClients.map(o => o.name)
            : [],
        },
        campaignFilter,
        {
          label: 'Merch Type',
          values: merchNames || [],
          sorted: false,
          onChange: () => {},
          onChangeMulti: onChangeMultiMerchTypes,
          selectedValue: selectedMerchTypes,
          multiSelectedValue: selectedMerchTypes ? selectedMerchTypes : [],
        },
      ]

  return (
    <div className="filter-wrapper">
      <div className="filter-left">
        <SearchAndFiltersWithCodeAutoComplete
          filters={filters}
          onSearchTextChange={rerenderAfter(setSearchText)}
          searchInputClassName={classes.searchInputClassName}
          allCodes={[
            ...givenOutCodes.map(givenCode => givenCode.code),
            ...availableCodes.map(availableCode => availableCode.code),
          ]}
          setSelectedCodes={setSelectedCodes}
          selectedCodes={selectedCodes}
          cityId={cityId}
        />
        {!isBriefPage(pickupType) && (
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={showOnlyBooked}
                onChange={(_, checked) => {
                  setShowOnlyBooked(checked)
                }}
              />
            }
            label="Show only clients with booked missions"
            cypress_id = {'showOnlyBookedCheckboxText'}
          />
        )}
      </div>
      <div className="wrapper">
        {isBriefPage(pickupType) && (
          <PresetToggle
            preset={selectedPreset}
            onChange={preset => setSelectedPreset(preset)}
          />
        )}
        <div className="clear-button">
          <Button onClickHandler={handlePickupBrief}>
            <FileDownloadIcon />
            Pickup {pickupType}
          </Button>
        </div>
      </div>
    </div>
  )
}
